// src/utils/storage.js

const STORAGE_KEYS = {
  PROFILE: 'sl_profile',
  THEME: 'sl_theme',
  PREFERENCES: 'sl_preferences',
  USER_ROLE: 'sl_user_role', // Added user role key
  // Add more keys as needed
};

/**
 * Storage utility for handling localStorage operations
 */
class StorageUtils {
  /**
   * Set an item in localStorage
   * @param {string} key - The storage key
   * @param {any} value - The value to store
   * @param {object} [options] - Additional options
   * @param {number} [options.expiry] - Expiry time in milliseconds
   */
  static setItem(key, value, options = {}) {
    try {
      const item = {
        value,
        timestamp: Date.now(),
        ...(options.expiry && { expiry: Date.now() + options.expiry }),
      };
      localStorage.setItem(key, JSON.stringify(item));
    } catch (error) {
      console.error(`Error setting ${key} in localStorage:`, error);
    }
  }

  /**
   * Get an item from localStorage
   * @param {string} key - The storage key
   * @param {object} [options] - Additional options
   * @param {any} [options.defaultValue] - Default value if item doesn't exist
   * @returns {any} The stored value or defaultValue
   */
  static getItem(key, options = {}) {
    try {
      const item = localStorage.getItem(key);
      if (!item) return options.defaultValue;

      const parsed = JSON.parse(item);

      // Check if item has expired
      if (parsed.expiry && parsed.expiry < Date.now()) {
        this.removeItem(key);
        return options.defaultValue;
      }

      return parsed.value;
    } catch (error) {
      console.error(`Error getting ${key} from localStorage:`, error);
      return options.defaultValue;
    }
  }

  /**
   * Remove an item from localStorage
   * @param {string} key - The storage key
   */
  static removeItem(key) {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Error removing ${key} from localStorage:`, error);
    }
  }

  /**
   * Clear all app-specific items from localStorage
   */
  static clearAppStorage() {
    try {
      Object.values(STORAGE_KEYS).forEach((key) => {
        this.removeItem(key);
      });
    } catch (error) {
      console.error('Error clearing app storage:', error);
    }
  }

  /**
   * Check if an item exists in localStorage
   * @param {string} key - The storage key
   * @returns {boolean} Whether the item exists and is valid
   */
  static hasItem(key) {
    try {
      const item = this.getItem(key);
      return item !== undefined && item !== null;
    } catch {
      return false;
    }
  }

  /**
   * Update a specific field in a stored object
   * @param {string} key - The storage key
   * @param {string} field - The field to update
   * @param {any} value - The new value
   */
  static updateField(key, field, value) {
    try {
      const currentValue = this.getItem(key, { defaultValue: {} });
      const updatedValue = {
        ...currentValue,
        [field]: value,
      };
      this.setItem(key, updatedValue);
    } catch (error) {
      console.error(`Error updating field ${field} in ${key}:`, error);
    }
  }

  /**
   * Check if user is admin
   * @returns {boolean}
   */
  static isAdmin() {
    try {
      const profile = this.getItem(STORAGE_KEYS.PROFILE, { defaultValue: {} });
      return profile?.userRole === 'admin';
    } catch {
      return false;
    }
  }

  /**
   * Get user role from storage
   * @returns {string|null}
   */
  static getUserRole() {
    try {
      const profile = this.getItem(STORAGE_KEYS.PROFILE, { defaultValue: {} });
      return profile?.userRole || null;
    } catch {
      return null;
    }
  }
}

export { StorageUtils, STORAGE_KEYS };
